import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import landingPageService from '../../services/landingPageService';
import {
  Plus,
  Search,
  Layout,
  Eye,
  Trash2,
  Edit,
  Copy,
  MoreVertical,
  RefreshCw,
  Grid3X3,
  List,
  ExternalLink,
  ShoppingBag,
  Globe,
  Calendar,
  Sparkles,
  TrendingUp,
  MousePointerClick,
  Users,
  BarChart3,
  Rocket,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
  Image as ImageIcon,
  Link2,
  Upload,
  Code,
  FileCode,
  X,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Select from '../../components/ui/Select';
import LeadsModal from '../../components/landing-pages/LeadsModal';
import HubDomainModal from '../../components/landing-pages/HubDomainModal';
import ImageLibraryPanel from '../../components/landing-pages/ImageLibraryPanel';
import ABTestModal from '../../components/landing-pages/ABTestModal';
import ABTestList from '../../components/landing-pages/ABTestList';

// Types
interface LandingPage {
  id: string;
  name: string;
  slug: string;
  description?: string;
  status: 'draft' | 'published' | 'archived';
  thumbnail?: string;
  views: number;
  conversions: number;
  conversionRate: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  publishedUrl?: string;
  customDomains?: string[];
}

// Prefer client-facing URLs over the vercel.app deployment URL:
// LP's own custom domain > hub domain (/slug) > deployment URL
// Mirror of the backend HUB_SLUG_RE — the hub sync skips slugs outside this
// format, so linking them via the hub would 404.
const HUB_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/i;

const getPublicUrl = (landingPage: LandingPage, hubDomain?: string | null): string | null => {
  if (landingPage.customDomains && landingPage.customDomains.length > 0) {
    return `https://${landingPage.customDomains[0]}`;
  }
  if (hubDomain && landingPage.slug && HUB_SLUG_RE.test(landingPage.slug)) {
    return `https://${hubDomain}/${landingPage.slug}`;
  }
  return landingPage.publishedUrl || null;
};

const LandingPagesPage: React.FC = () => {
  const { t } = useTranslation();
  const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pages' | 'abtests' | 'images'>('pages');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showHubModal, setShowHubModal] = useState(false);
  // Primeiro domínio verificado do hub — vira a base dos links das LPs
  const [hubDomain, setHubDomain] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'file' | 'code'>('file');
  const [importName, setImportName] = useState('');
  const [importCode, setImportCode] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Leads modal state
  const [leadsModal, setLeadsModal] = useState<{ isOpen: boolean; pageId: string; pageName: string }>({
    isOpen: false,
    pageId: '',
    pageName: ''
  });

  // A/B Test modal state
  const [abTestModal, setAbTestModal] = useState<{ isOpen: boolean; pageId: string; pageName: string }>({
    isOpen: false,
    pageId: '',
    pageName: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 12;

  // Load landing pages from backend
  const loadLandingPages = useCallback(async (searchTerm?: string, status?: string, page: number = 1) => {
    try {
      setIsLoading(true);
      const response = await landingPageService.getLandingPages({
        search: searchTerm || undefined,
        status: status || undefined,
        page,
        limit: itemsPerPage
      });
      if (response.success && response.data) {
        // Map backend fields to frontend interface
        const pages = response.data.map((page: any) => ({
          id: page.id,
          name: page.name,
          slug: page.slug,
          description: page.meta_description,
          status: page.status,
          thumbnail: page.thumbnail || page.og_image || null,
          views: page.views || 0,
          conversions: page.conversions || 0,
          conversionRate: page.views > 0 ? (page.conversions / page.views) * 100 : 0,
          createdAt: page.createdAt || page.created_at,
          updatedAt: page.updatedAt || page.updated_at,
          publishedAt: page.last_deployed_at,
          publishedUrl: page.published_url,
          customDomains: page.custom_domains || []
        }));
        setLandingPages(pages);

        // Update pagination info
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.total);
          setCurrentPage(response.pagination.page);
        }
      }
    } catch (error) {
      console.error('Error loading landing pages:', error);
      toast.error(t('landingPages.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const loadHub = useCallback(async () => {
    try {
      const response = await landingPageService.getHub();
      if (response.success && response.data?.exists) {
        // Só usa o domínio como base dos links quando o DNS realmente aponta
        // (configured) — "verified" sozinho não garante que o endereço resolve
        const active = response.data.domains.find(d => d.verified && d.configured);
        setHubDomain(active ? active.name : null);
      } else {
        setHubDomain(null);
      }
    } catch (error) {
      // hub é opcional — falha aqui não deve quebrar a listagem
      console.error('Error loading hub:', error);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadLandingPages();
    loadHub();
  }, [loadLandingPages, loadHub]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadLandingPages(search, statusFilter, 1);
  };

  const handleRefresh = () => {
    loadLandingPages(search, statusFilter, currentPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadLandingPages(search, statusFilter, page);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('landingPages.confirmDelete'))) return;
    try {
      const response = await landingPageService.deleteLandingPage(id);
      if (response.success) {
        setLandingPages(prev => prev.filter(lp => lp.id !== id));
        toast.success(t('landingPages.deleted'));
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error(error.message || t('landingPages.deleteError'));
    }
    setOpenMenu(null);
  };

  const handlePreview = async (landingPage: LandingPage) => {
    try {
      // Get full page data
      const response = await landingPageService.getLandingPage(landingPage.id);
      if (!response.success || !response.data) {
        toast.error('Error loading page');
        return;
      }

      const page = response.data;
      const html = page.gjs_html || '';
      let css = page.gjs_css || '';
      const headCode = page.head_code || '';
      const bodyEndCode = page.body_end_code || '';

      // Imported HTML pages have gjs_html but no gjs_components — render them
      // passthrough (no Tailwind/Inter injection) to preserve original design,
      // and include head_code (fonts/links) + body_end_code (scripts).
      const isImportedHtml = !!page.gjs_html && (!page.gjs_components || page.gjs_components === '' || page.gjs_components === '[]');

      // Documento HTML COMPLETO (importado/editado inteiro) — pré-visualiza
      // verbatim, sem qualquer wrapper.
      const isFullDoc = !!page.gjs_html && /^\s*(<!doctype\s+html|<html[\s>])/i.test(page.gjs_html);
      if (isFullDoc) {
        const blob = new Blob([page.gjs_html || ''], { type: 'text/html' });
        window.open(URL.createObjectURL(blob), '_blank');
        setOpenMenu(null);
        return;
      }

      // Try to extract wrapper/body styles from gjs_styles if not in css
      // This handles pages saved before the wrapper styles fix
      if (page.gjs_styles && !css.includes('body {')) {
        try {
          const styles = JSON.parse(page.gjs_styles);
          // Find wrapper styles (GrapesJS stores wrapper as first element or with selector 'wrapper')
          const wrapperStyle = styles.find((s: { selectors?: string[]; style?: Record<string, string> }) =>
            s.selectors?.includes('wrapper') || s.selectors?.includes('#wrapper')
          );
          if (wrapperStyle?.style) {
            const wrapperCss = Object.entries(wrapperStyle.style)
              .map(([prop, val]) => `${prop}: ${val};`)
              .join(' ');
            css = `body { ${wrapperCss} }\n${css}`;
          }
        } catch (e) {
          console.warn('Could not parse gjs_styles for wrapper:', e);
        }
      }

      const fullHtml = isImportedHtml
        ? `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${landingPage.name} - Preview</title>
  ${headCode}
  <style>
    ${css}
  </style>
</head>
<body>
  ${html}
  ${bodyEndCode}
</body>
</html>`
        : `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${landingPage.name} - Preview</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
  ${headCode}
  <style>
    body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; }
    ${css}
  </style>
</head>
<body>
  ${html}
  ${bodyEndCode}
</body>
</html>`;

      const blob = new Blob([fullHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Error opening preview');
    }
    setOpenMenu(null);
  };

  const handleCopyLink = async (landingPage: LandingPage) => {
    const url = getPublicUrl(landingPage, hubDomain);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('landingPages.linkCopied', 'Link copiado!'));
    } catch (error) {
      // Fallback for browsers without clipboard permission
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(t('landingPages.linkCopied', 'Link copiado!'));
    }
    setOpenMenu(null);
  };

  const handleDuplicate = async (landingPage: LandingPage) => {
    try {
      // First get the full page data including content
      const fullPageResponse = await landingPageService.getLandingPage(landingPage.id);
      if (!fullPageResponse.success || !fullPageResponse.data) {
        throw new Error('Erro ao carregar página');
      }

      const fullPage = fullPageResponse.data;

      // Create a copy
      const response = await landingPageService.createLandingPage({
        name: `${landingPage.name} (${t('common.copy')})`,
        slug: `${landingPage.slug}-copia-${Date.now()}`,
        gjs_html: fullPage.gjs_html,
        gjs_css: fullPage.gjs_css,
        gjs_components: fullPage.gjs_components,
        gjs_styles: fullPage.gjs_styles,
        meta_title: fullPage.meta_title,
        meta_description: fullPage.meta_description
      });

      if (response.success && response.data) {
        // Reload list
        loadLandingPages(search, statusFilter);
        toast.success(t('landingPages.duplicated'));
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      console.error('Duplicate error:', error);
      toast.error(error.message || t('landingPages.duplicateError'));
    }
    setOpenMenu(null);
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { variant: 'success' | 'warning' | 'danger' | 'gray' | 'primary'; label: string }> = {
      draft: { variant: 'gray', label: t('landingPages.status.draft') },
      published: { variant: 'success', label: t('landingPages.status.published') },
      archived: { variant: 'warning', label: t('landingPages.status.archived') }
    };
    return config[status] || { variant: 'gray', label: status };
  };

  const filteredPages = statusFilter
    ? landingPages.filter(lp => lp.status === statusFilter)
    : landingPages;

  // Calculate stats
  const stats = {
    total: landingPages.length,
    published: landingPages.filter(lp => lp.status === 'published').length,
    totalViews: landingPages.reduce((sum, lp) => sum + lp.views, 0),
    totalConversions: landingPages.reduce((sum, lp) => sum + lp.conversions, 0),
    avgConversionRate: landingPages.filter(lp => lp.conversionRate > 0).length > 0
      ? (landingPages.reduce((sum, lp) => sum + lp.conversionRate, 0) / landingPages.filter(lp => lp.conversionRate > 0).length).toFixed(2)
      : '0'
  };

  const statusOptions = [
    { value: '', label: t('landingPages.filters.allStatus') },
    { value: 'draft', label: t('landingPages.filters.draft') },
    { value: 'published', label: t('landingPages.filters.published') },
    { value: 'archived', label: t('landingPages.filters.archived') }
  ];

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <PageHeader
        icon={Layout}
        title={t('landingPages.title')}
        description={t('landingPages.subtitle')}
        actions={activeTab === 'pages' ? (
          <>
            {/* No celular: só os ícones, para caber numa linha */}
            <Button variant="secondary" onClick={() => setShowHubModal(true)} leftIcon={<Globe className="w-4 h-4" />} aria-label={t('landingPages.hub.button', 'Domínio único')} title={t('landingPages.hub.button', 'Domínio único')}>
              <span className="hidden sm:inline">{t('landingPages.hub.button', 'Domínio único')}</span>
            </Button>
            <Button variant="secondary" onClick={handleRefresh} leftIcon={<RefreshCw className="w-4 h-4" />} aria-label={t('landingPages.refresh')} title={t('landingPages.refresh')}>
              <span className="hidden sm:inline">{t('landingPages.refresh')}</span>
            </Button>
            <Link to="/landing-pages/new" className="flex-1 sm:flex-none [&>button]:w-full [&>button]:h-10">
              <Button leftIcon={<Plus className="w-4 h-4" />}>
                {t('landingPages.newPage')}
              </Button>
            </Link>
          </>
        ) : undefined}
      />

      {/* Aqui só se fazem páginas de venda; loja virtual completa e venda de cursos ficam no Marketplace */}
      <a
        href="https://admin-marketplace.devskin.com/site-design"
        target="_blank"
        rel="noopener noreferrer"
        className="group flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-emerald-200 dark:border-emerald-900/60 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/30 p-4 hover:shadow-md transition-shadow"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow">
          <ShoppingBag className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-gray-900 dark:text-white">
            {t('landingPages.storeCta.title', 'Precisa de uma loja virtual ou vender cursos online?')}
          </span>
          <span className="block text-sm text-gray-600 dark:text-gray-300">
            {t('landingPages.storeCta.desc', 'Crie seu site de vendas completo pelo Marketplace: produtos, carrinho, pagamento, frete, nota fiscal e área de alunos.')}
          </span>
        </span>
        <span className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 group-hover:bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white whitespace-nowrap">
          {t('landingPages.storeCta.button', 'Criar minha loja')} <ExternalLink className="h-4 w-4" />
        </span>
      </a>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="grid grid-cols-3 sm:flex sm:gap-4">
          <button
            onClick={() => setActiveTab('pages')}
            className={`min-w-0 flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 pb-3 px-1 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === 'pages'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Layout className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{t('landingPages.tabs.pages', 'Páginas')}</span>
          </button>
          <button
            onClick={() => setActiveTab('abtests')}
            className={`min-w-0 flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 pb-3 px-1 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === 'abtests'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <FlaskConical className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{t('abTesting.title', 'Testes A/B')}</span>
          </button>
          <button
            onClick={() => setActiveTab('images')}
            className={`min-w-0 flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 pb-3 px-1 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === 'images'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <ImageIcon className="w-4 h-4 flex-shrink-0" />
            <span className="truncate sm:hidden">{t('landingPages.tabs.imagesShort', 'Imagens')}</span>
            <span className="hidden sm:inline">{t('landingPages.tabs.images', 'Banco de imagens')}</span>
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'images' ? (
        <ImageLibraryPanel />
      ) : activeTab === 'abtests' ? (
        <ABTestList />
      ) : (
        <>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="!p-3 sm:!p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-blue-600 dark:text-blue-400">{t('landingPages.stats.totalPages')}</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.total}</p>
                <p className="hidden sm:flex text-xs text-blue-600 dark:text-blue-400 items-center mt-1">
                  <Layout className="w-3 h-3 mr-1" />
                  {stats.published} {t('landingPages.stats.published')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-blue-100 dark:bg-blue-800 rounded-xl items-center justify-center">
                <Layout className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="!p-3 sm:!p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <CardContent className="sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-green-600 dark:text-green-400">{t('landingPages.stats.totalViews')}</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.totalViews.toLocaleString('pt-BR')}</p>
                <p className="hidden sm:flex text-xs text-green-600 dark:text-green-400 items-center mt-1">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {t('landingPages.stats.totalViews')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-green-100 dark:bg-green-800 rounded-xl items-center justify-center">
                <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="!p-3 sm:!p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <CardContent className="sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-purple-600 dark:text-purple-400">{t('landingPages.stats.conversions')}</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.totalConversions.toLocaleString('pt-BR')}</p>
                <p className="hidden sm:flex text-xs text-purple-600 dark:text-purple-400 items-center mt-1">
                  <MousePointerClick className="w-3 h-3 mr-1" />
                  {t('landingPages.stats.conversions')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-purple-100 dark:bg-purple-800 rounded-xl items-center justify-center">
                <MousePointerClick className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="!p-3 sm:!p-6 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200 dark:border-orange-800">
          <CardContent className="sm:pt-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium truncate text-orange-600 dark:text-orange-400">{t('landingPages.stats.conversionRate')}</p>
                <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">{stats.avgConversionRate}%</p>
                <p className="hidden sm:flex text-xs text-orange-600 dark:text-orange-400 items-center mt-1">
                  <BarChart3 className="w-3 h-3 mr-1" />
                  {t('landingPages.stats.average')}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-orange-100 dark:bg-orange-800 rounded-xl items-center justify-center">
                <BarChart3 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Link to="/landing-pages/new" className="block">
          <div className="h-full p-3 sm:p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md hover:border-primary-300 dark:hover:border-primary-700 transition-all cursor-pointer group">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{t('landingPages.quickActions.createWithAi')}</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{t('landingPages.quickActions.createWithAiDesc')}</p>
              </div>
            </div>
          </div>
        </Link>
        <Link to="/landing-pages/new?template=blank" className="block">
          <div className="h-full p-3 sm:p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md hover:border-primary-300 dark:hover:border-primary-700 transition-all cursor-pointer group">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                <Layout className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{t('landingPages.quickActions.blankPage')}</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{t('landingPages.quickActions.blankPageDesc')}</p>
              </div>
            </div>
          </div>
        </Link>
        <Link to="/landing-pages/new?template=templates" className="block">
          <div className="h-full p-3 sm:p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md hover:border-primary-300 dark:hover:border-primary-700 transition-all cursor-pointer group">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                <Grid3X3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{t('landingPages.quickActions.useTemplate')}</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{t('landingPages.quickActions.useTemplateDesc')}</p>
              </div>
            </div>
          </div>
        </Link>
        <div role="button" tabIndex={0} onClick={() => setShowImportModal(true)} onKeyDown={(e) => { if (e.key === 'Enter') setShowImportModal(true); }} className="block cursor-pointer">
          <div className="h-full p-3 sm:p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md hover:border-primary-300 dark:hover:border-primary-700 transition-all group">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                <FileCode className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{t('landingPages.importHtml')}</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{t('landingPages.importHtmlDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import HTML Modal */}
      {showImportModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowImportModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('landingPages.importTitle')}</h2>
                <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('landingPages.importName')} *</label>
                  <input
                    type="text"
                    value={importName}
                    onChange={(e) => setImportName(e.target.value)}
                    placeholder="Ex: Minha Landing Page"
                    className="input"
                  />
                </div>

                {/* Mode Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setImportMode('file')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      importMode === 'file'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    <Upload className="w-5 h-5" />
                    {t('landingPages.importUploadFile')}
                  </button>
                  <button
                    onClick={() => setImportMode('code')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      importMode === 'code'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    <Code className="w-5 h-5" />
                    {t('landingPages.importPasteCode')}
                  </button>
                </div>

                {/* File Upload */}
                {importMode === 'file' && (
                  <div>
                    <label className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors">
                      {importFile ? (
                        <>
                          <FileCode className="w-10 h-10 text-green-500" />
                          <div className="text-center">
                            <p className="font-medium text-gray-900 dark:text-white">{importFile.name}</p>
                            <p className="text-sm text-gray-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setImportFile(null); }}
                            className="text-sm text-red-500 hover:text-red-700"
                          >
                            {t('landingPages.importRemove')}
                          </button>
                        </>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-gray-400" />
                          <div className="text-center">
                            <p className="font-medium text-gray-700 dark:text-gray-300">{t('landingPages.importSelectFile')}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{t('landingPages.importFileLimit')}</p>
                          </div>
                        </>
                      )}
                      <input
                        type="file"
                        accept=".html,.htm"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) setImportFile(f);
                        }}
                      />
                    </label>
                  </div>
                )}

                {/* Code Input */}
                {importMode === 'code' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('landingPages.importCodeLabel')}</label>
                    <textarea
                      value={importCode}
                      onChange={(e) => setImportCode(e.target.value)}
                      placeholder="<!DOCTYPE html>\n<html>\n<head>...</head>\n<body>...</body>\n</html>"
                      rows={12}
                      className="input font-mono text-sm"
                      style={{ resize: 'vertical' }}
                    />
                    <p className="text-xs text-gray-400 mt-1">{t('landingPages.importCodeHint')}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={() => setShowImportModal(false)}>{t('common.cancel')}</Button>
                <Button
                  disabled={!importName || isImporting || (importMode === 'file' ? !importFile : !importCode)}
                  onClick={async () => {
                    try {
                      setIsImporting(true);
                      const result = await landingPageService.importHtmlLandingPage({
                        name: importName,
                        file: importMode === 'file' ? importFile! : undefined,
                        html: importMode === 'code' ? importCode : undefined
                      });
                      if (result.success) {
                        toast.success(t('landingPages.importSuccess'));
                        setShowImportModal(false);
                        setImportName('');
                        setImportCode('');
                        setImportFile(null);
                        loadLandingPages();
                      } else {
                        toast.error(result.message || 'Erro ao importar');
                      }
                    } catch (err: any) {
                      toast.error(err.response?.data?.message || 'Erro ao importar landing page');
                    } finally {
                      setIsImporting(false);
                    }
                  }}
                >
                  {isImporting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('landingPages.importing')}</> : t('landingPages.importButton')}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Search & Filters */}
      <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2 max-w-md">
            <Input
              placeholder={t('landingPages.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-5 h-5" />}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('landingPages.searchButton')}
            </Button>
          </form>

          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0 sm:flex-none sm:w-40">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
            <div className="flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                aria-label={t('landingPages.viewGrid', 'Ver em cartões')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                aria-label={t('landingPages.viewList', 'Ver em lista')}
                className={`p-2 ${viewMode === 'list' ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Landing Pages Grid/List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-72 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredPages.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Layout className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('landingPages.empty.title')}</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">{t('landingPages.empty.description')}</p>
              <Link to="/landing-pages/new">
                <Button leftIcon={<Sparkles className="w-4 h-4" />}>
                  {t('landingPages.empty.create')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {filteredPages.map((landingPage) => {
            const statusConfig = getStatusBadge(landingPage.status);

            return (
              <Card key={landingPage.id} className="hover:shadow-lg transition-all group !overflow-visible">
                {/* Thumbnail */}
                <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                  {landingPage.thumbnail ? (
                    <img
                      src={landingPage.thumbnail}
                      alt={landingPage.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Layout className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Link to={`/landing-pages/${landingPage.id}/edit`}>
                      <Button size="sm" variant="secondary" leftIcon={<Edit className="w-4 h-4" />}>
                        {t('landingPages.actions.edit')}
                      </Button>
                    </Link>
                    <Button size="sm" variant="secondary" leftIcon={<Eye className="w-4 h-4" />} onClick={() => handlePreview(landingPage)}>
                      {t('landingPages.actions.preview')}
                    </Button>
                  </div>
                </div>

                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {landingPage.name}
                    </h3>
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenu(openMenu === landingPage.id ? null : landingPage.id)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-500" />
                      </button>
                      {openMenu === landingPage.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                          <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                            <Link
                              to={`/landing-pages/${landingPage.id}/edit`}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                            >
                              <Edit className="w-4 h-4" />
                              {t('landingPages.actions.edit')}
                            </Link>
                            <button
                              onClick={() => handlePreview(landingPage)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                            >
                              <Eye className="w-4 h-4" />
                              {t('landingPages.actions.preview')}
                            </button>
                            {landingPage.status === 'published' && getPublicUrl(landingPage, hubDomain) && (
                              <>
                                <a
                                  href={getPublicUrl(landingPage, hubDomain)!}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  {t('landingPages.actions.viewLive')}
                                </a>
                                <button
                                  onClick={() => handleCopyLink(landingPage)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                                >
                                  <Link2 className="w-4 h-4" />
                                  {t('landingPages.actions.copyLink', 'Copiar link')}
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                setLeadsModal({ isOpen: true, pageId: landingPage.id, pageName: landingPage.name });
                                setOpenMenu(null);
                              }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                            >
                              <Users className="w-4 h-4" />
                              {t('landingPages.actions.viewLeads', 'Ver Leads')}
                            </button>
                            <button
                              onClick={() => handleDuplicate(landingPage)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                            >
                              <Copy className="w-4 h-4" />
                              {t('landingPages.actions.duplicate')}
                            </button>
                            {landingPage.status === 'published' && (
                              <button
                                onClick={() => {
                                  setAbTestModal({ isOpen: true, pageId: landingPage.id, pageName: landingPage.name });
                                  setOpenMenu(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                              >
                                <FlaskConical className="w-4 h-4" />
                                {t('abTesting.createTest', 'Criar Teste A/B')}
                              </button>
                            )}
                            <hr className="my-1 border-gray-200 dark:border-gray-700" />
                            <button
                              onClick={() => handleDelete(landingPage.id)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 w-full"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('landingPages.actions.delete')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {landingPage.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                      {landingPage.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {landingPage.views.toLocaleString('pt-BR')}
                    </span>
                    <span className="flex items-center gap-1">
                      <MousePointerClick className="w-3.5 h-3.5" />
                      {landingPage.conversions.toLocaleString('pt-BR')}
                    </span>
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                      {landingPage.conversionRate.toFixed(2)}%
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(landingPage.createdAt).toLocaleDateString('pt-BR')}
                    </span>
                    {landingPage.status === 'published' && (
                      <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                        <Globe className="w-3 h-3" />
                        {t('landingPages.status.online')}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <Card padding={false}>
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    {t('landingPages.table.name')}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    {t('landingPages.table.status')}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    {t('landingPages.table.views')}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    {t('landingPages.table.conversions')}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    {t('landingPages.table.rate')}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    {t('landingPages.table.createdAt')}
                  </th>
                  <th className="px-2 sm:px-4 py-3 sm:py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredPages.map((landingPage) => {
                  const statusConfig = getStatusBadge(landingPage.status);

                  return (
                    <tr key={landingPage.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-10 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden flex-shrink-0">
                            {landingPage.thumbnail ? (
                              <img src={landingPage.thumbnail} alt={landingPage.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Layout className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{landingPage.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">/{landingPage.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4">
                        <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{landingPage.views.toLocaleString('pt-BR')}</span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{landingPage.conversions.toLocaleString('pt-BR')}</span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <span className={`text-sm font-medium ${landingPage.conversionRate >= 5 ? 'text-green-600' : landingPage.conversionRate >= 2 ? 'text-yellow-600' : 'text-gray-600'}`}>
                          {landingPage.conversionRate.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(landingPage.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4">
                        <div className="flex items-center gap-1">
                          <Link to={`/landing-pages/${landingPage.id}/edit`}>
                            <Button variant="ghost" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(landingPage)} title="Preview">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Ver Leads"
                            onClick={() => setLeadsModal({ isOpen: true, pageId: landingPage.id, pageName: landingPage.name })}
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <button
                            onClick={() => handleDelete(landingPage.id)}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && !isLoading && (
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} páginas
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* AI Banner */}
      <div className="p-6 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Rocket className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{t('landingPages.aiBanner.title')}</h3>
              <p className="text-sm opacity-90">
                {t('landingPages.aiBanner.description')}
              </p>
            </div>
          </div>
          <Link to="/landing-pages/new" className="flex-shrink-0">
            <Button variant="secondary" className="bg-white text-primary-600 hover:bg-gray-100">
              {t('landingPages.aiBanner.cta')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Leads Modal */}
      <HubDomainModal
        isOpen={showHubModal}
        onClose={() => setShowHubModal(false)}
        onHubChanged={loadHub}
      />

      <LeadsModal
        isOpen={leadsModal.isOpen}
        onClose={() => setLeadsModal({ isOpen: false, pageId: '', pageName: '' })}
        landingPageId={leadsModal.pageId}
        landingPageName={leadsModal.pageName}
      />

      {/* A/B Test Modal */}
      <ABTestModal
        isOpen={abTestModal.isOpen}
        onClose={() => setAbTestModal({ isOpen: false, pageId: '', pageName: '' })}
        landingPageId={abTestModal.pageId}
        landingPageName={abTestModal.pageName}
        onTestCreated={() => {
          setAbTestModal({ isOpen: false, pageId: '', pageName: '' });
          setActiveTab('abtests');
        }}
      />
        </>
      )}
    </div>
  );
};

export default LandingPagesPage;
